// hooks/useApiCall.ts

import { useCallback } from 'react';

import { useChatOverlayContext } from '../context/chatOverlayContext';
import {
  type AskApiRequest,
  type AskApiResponse,
  ErrorTypeEnum,
  type GenericAskApiErrorResponse,
  type SampleDataApiRequest,
  type SampleDataAPIResponse,
} from '../data-layer/types';
import { Logger } from '../services';
import { useInvertedLocalize } from '../utils/localization';
import { invertedMessages } from '../utils/messages';

import useContextManagement from './use-contextmanagement';
import useResponseHandlers from './use-responsehandlers';

/**
 * A custom hook that provides a unified API call handler for handling various API requests
 * (e.g., Ask API or Sample Data API). This hook manages the request lifecycle, updates
 * context before and after the API call, and handles different response types.
 *
 * @returns {Object} - The handleApiCall function to execute API requests.
 */
const useApiCall = () => {
  const { updateContextAfterMakingApiCall, updateContextBeforeMakingApiCall } =
    useContextManagement();

  const l = useInvertedLocalize(invertedMessages);
  const { chatOverlayState } = useChatOverlayContext();
  const {
    handleErrorResponse,
    handleHighConfidenceChartResponse,
    handleHighConfidenceTableResponse,
    handleLowConfidenceChartResponse,
    handleLowConfidenceTabularResponse,
    handleRandomResponse,
    handleSampleDataChartResponse,
    handleSampleDataTableResponse,
  } = useResponseHandlers();

  /**
   * Handles an API call, processing the request and response. This function performs
   * the following steps:
   * 1. Updates the context before making the API call.
   * 2. Makes the API request using the provided `apiCall` function.
   * 3. Handles different types of responses based on the content type or confidence level.
   * 4. Updates the context after the API call and logs relevant information.
   * 5. Catches and handles errors during the API request.
   *
   * @template TRequest - The type of the request object (e.g., AskApiRequest, SampleDataApiRequest).
   * @template TResponse - The type of the response object (e.g., AskApiResponse, SampleDataAPIResponse).
   *
   * @param {TRequest} request - The API request payload.
   * @param {(request: TRequest, url: string) => Promise<TResponse>} apiCall - The API function to be called.
   * @param {string} url - The URL to which the API request is sent.
   *
   * @returns {Promise<void>} A promise that resolves when the API call completes or fails.
   */
  const handleApiCall = useCallback(
    async <
      TRequest extends AskApiRequest | SampleDataApiRequest,
      TResponse extends AskApiResponse | SampleDataAPIResponse,
    >(
      request: TRequest,
      apiCall: (request: TRequest, url: string) => Promise<TResponse>,
      url: string
    ) => {
      if (chatOverlayState.isApiCallInProgress) {
        Logger.info(
          'handleApiCall --> Api call is in progress. blocking api call'
        );

        return;
      }

      updateContextBeforeMakingApiCall(request);

      try {
        const response = await apiCall(request, url);

        // To mock the api call, comment above line. uncomment below 2 linwa.
        // await Promise.resolve(() => console.log({ apiCall, url, request }));
        // const response = highConfidenceSQLError as TResponse;

        Logger.info('RESPONSE from API call', { chatData: response });

        if ('contentType' in response) {
          updateContextAfterMakingApiCall(response, request);

          switch (response.contentType) {
            case 'text':
              // If content type is text - it means general response
              handleRandomResponse();
              break;

            case 'sampleData':
              if (response.confidence === 'low') {
                handleSampleDataTableResponse();
              } else {
                handleHighConfidenceTableResponse();
              }

              break;

            case 'chart':
              if (response.confidence === 'low') {
                handleSampleDataChartResponse();
              } else {
                handleHighConfidenceChartResponse();
              }

              break;

            case 'query':
              if ('error' in response) {
                handleErrorResponse(response.error.type);
                Logger.log(
                  'Error scenario, There can be any type of error that can happen.'
                );
              } else if ('confidence' in response) {
                Logger.log('no error - low confidence scenario');

                if ('chartSpec' in response) {
                  handleLowConfidenceChartResponse();
                  Logger.log('handle chart low confidence ask api response');
                } else {
                  handleLowConfidenceTabularResponse();
                  Logger.log('handle table low confidence ask api response');
                }
              }

              break;

            default:
              // Handle generic error
              handleErrorResponse(ErrorTypeEnum.System);
              Logger.warn('Unexpected content type:', response);
              break;
          }
        } else if ('error' in response) {
          updateContextAfterMakingApiCall(response, request);
          // Handle generic error
          handleErrorResponse(response.error.type);
          Logger.warn('Response does not have contentType property:', response);
        } else {
          throw Error('Response structure not recognised');
        }
      } catch (err) {
        const errResponse: GenericAskApiErrorResponse = {
          // Update with current chatuuid because server will have this chatuuid.
          // code will reach here if we are not able to hit server.
          chatuuid: 'error',
          error: {
            errorCode: 'I1001',
            errorMsg: l(
              'Sorry, there was an error or interruption while processing your prompt. When the service becomes available again, you can either enter your original prompt or try a new one'
            ),
            type: ErrorTypeEnum.System,
          },
        };

        Logger.error('Error in API call', { err, errResponse });
        updateContextAfterMakingApiCall(errResponse, request);
        handleErrorResponse(errResponse.error.type);
      }
    },
    [
      chatOverlayState.isApiCallInProgress,
      updateContextBeforeMakingApiCall,
      updateContextAfterMakingApiCall,
      handleRandomResponse,
      handleSampleDataTableResponse,
      handleHighConfidenceTableResponse,
      handleSampleDataChartResponse,
      handleHighConfidenceChartResponse,
      handleErrorResponse,
      handleLowConfidenceChartResponse,
      handleLowConfidenceTabularResponse,
      l,
    ]
  );

  return { handleApiCall };
};

export default useApiCall;
